import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { Controller, useForm } from 'react-hook-form';
import { Box } from '@mui/material';
import ExpandableTextArea from 'components/ExpandableTextArea';
import MDInput from 'components/MDInput';
import MDFormulaInput from 'components/MDFormulaInput';

export function SimpleMailOutputNodeForm({ id, data, onUpdateData }) {
  const { formatMessage: msg } = useIntl();

  const { control, setValue, watch } = useForm({
    defaultValues: {
      protocol: data?.protocol || 'smtp',
      hostURI: data?.hostURI || '',
      port: data?.port || '465',
      username: data?.username || '',
      password: data?.password || '',
      subject: data?.subject || '',
      recipients: data?.recipients || '',
      template: data?.template || ''
    }
  });

  const formValues = watch();

  useEffect(() => {
    onUpdateData?.({
      id,
      data: {
        ...data,
        protocol: formValues.protocol,
        hostURI: formValues.hostURI,
        port: formValues.port,
        username: formValues.username,
        password: formValues.password,
        subject: formValues.subject,
        recipients: formValues.recipients,
        template: formValues.template
      }
    });
  }, [formValues, data, id, onUpdateData]);

  const clearField = (fieldName) => {
    setValue(fieldName, '');
  };

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Controller
        sx={{ mb: 2 }}
        name="protocol"
        control={control}
        render={({ field }) => (
          <MDInput
            {...field}
            label={msg({ id: 'field.mail.protocol.label' })}
            variant="filled"
            fullWidth
            size="small"
          />
        )}
      />

      <Controller
        sx={{ mb: 2 }}
        name="hostURI"
        control={control}
        render={({ field }) => (
          <MDInput
            {...field}
            label={msg({ id: 'field.mail.host.label' })}
            variant="filled"
            fullWidth
            size="small"
          />
        )}
      />

      <Controller
        sx={{ mb: 2 }}
        name="port"
        control={control}
        rules={{
          valueAsNumber: true
        }}
        render={({ field }) => (
          <MDInput
            {...field}
            label={msg({ id: 'field.mail.port.label' })}
            variant="filled"
            fullWidth
            size="small"
          />
        )}
      />

      <Controller
        sx={{ mb: 2 }}
        name="username"
        control={control}
        render={({ field }) => (
          <MDInput
            {...field}
            label={msg({ id: 'field.mail.username.label' })}
            variant="filled"
            fullWidth
            size="small"
            autocomplete="off"
          />
        )}
      />

      <Controller
        sx={{ mb: 2 }}
        name="password"
        control={control}
        render={({ field }) => (
          <MDInput
            {...field}
            type="password"
            label={msg({ id: 'field.mail.password.label' })}
            variant="filled"
            fullWidth
            size="small"
            autoComplete="new-password"
          />
        )}
      />

      <Controller
        sx={{ mb: 2 }}
        name="recipients"
        control={control}
        render={({ field }) => (
          <MDFormulaInput
            {...field}
            label={msg({ id: 'field.mail.recipients.label' })}
            variant="filled"
            fullWidth
            size="small"
          />
        )}
      />

      <Controller
        sx={{ mb: 2 }}
        name="subject"
        control={control}
        render={({ field }) => (
          <MDFormulaInput
            {...field}
            label={msg({ id: 'field.mail.subject.label' })}
            variant="filled"
            fullWidth
            size="small"
          />
        )}
      />

      <Controller
        name="template"
        control={control}
        render={({ field }) => (
          <ExpandableTextArea
            field={field}
            clearField={() => clearField('template')}
            label={msg({ id: 'field.template.label' })}
            control={control}
            minRows={3}
            maxRows={100}
          />
        )}
      />
    </Box>
  );
}

SimpleMailOutputNodeForm.propTypes = {
  id: PropTypes.string,
  data: PropTypes.shape({
    id: PropTypes.number,
    label: PropTypes.string,
    protocol: PropTypes.string,
    hostURI: PropTypes.string,
    port: PropTypes.string,
    username: PropTypes.string,
    password: PropTypes.string,
    subject: PropTypes.string,
    recipients: PropTypes.string,
    template: PropTypes.string
  }),
  onUpdateData: PropTypes.func.isRequired
};
